

// #############################################################

// Smooth animation

import React, { useRef, useLayoutEffect, useState } from "react";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Tooltip from "@mui/material/Tooltip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import FacebookRoundedIcon from "@mui/icons-material/FacebookRounded";
import YouTubeIcon from "@mui/icons-material/YouTube";
import InstagramIcon from "@mui/icons-material/Instagram";
import facebookVerifiedIcon from "assets/images/icons/socials/facebook-verified.png";
import youtubeVerifiedIcon from "assets/images/icons/socials/youtube-verified.png";
import { motion } from "framer-motion";
import KeyMetrics from "../components/KeyMetrics";

/**
 * 🔄 AnimatedHeight wrapper
 */
function AnimatedHeight({ children, duration = 0.6, ease = [0.25, 0.8, 0.25, 1] }) {
  const ref = useRef(null);
  const [height, setHeight] = useState("auto");

  useLayoutEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    let frame;

    const updateHeight = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setHeight(el.clientHeight));
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(el);

    return () => {
      cancelAnimationFrame(frame);
      resizeObserver.disconnect();
    };
  }, [children]);

  return (
    <motion.div
      animate={{ height }}
      initial={false}
      transition={{ duration, ease }}
      style={{
        overflow: "visible",
        transformOrigin: "top center",
      }}
    >
      <div ref={ref}>{children}</div>
    </motion.div>
  );
}

/**
 * 🧩 ExecutiveSummaryProfileCard
 */
export default function ExecutiveSummaryProfileCard({
  profile,
  datasets,
  previousdatasets,
  adjustedcurrentdatasets,
  adjustedpreviousdatasets,
  selectedCategories,

  // ⭐ NEW PROP
  hideKeyMetrics,
}) {
  const { label, profilePictureUrl, platform, verifiedStatus, link } = profile;

  const platformIcon = {
    Facebook: <FacebookRoundedIcon fontSize="small" sx={{ ml: 0.5, color: "#282828" }} />,
    YouTube: <YouTubeIcon fontSize="small" sx={{ ml: 0.5, color: "#282828" }} />,
    Instagram: <InstagramIcon fontSize="small" sx={{ ml: 0.5, color: "#282828" }} />,
  }[platform];

  const verifiedIcon =
    platform === "Facebook"
      ? facebookVerifiedIcon
      : platform === "YouTube"
        ? youtubeVerifiedIcon
        : null;

  return (
    <Card
      sx={{
        mt: 2,
        pt: 2,
        px: 3,
        pb: 3,
        borderRadius: "16px",
        boxShadow: "0 3px 12px rgba(0,0,0,0.08)",
        backgroundColor: "#f9f9f9",
        width: "100%",
        overflow: "visible",
      }}
    >
      {/* Header */}
      <MDBox display="flex" alignItems="center" mb={2}>
        <Box sx={{ position: "relative", mr: 1 }}>
          <Tooltip title={label} arrow>
            <a href={link} target="_blank" rel="noopener noreferrer">
              <Avatar src={profilePictureUrl} alt={label} sx={{ width: 50, height: 50 }} />
            </a>
          </Tooltip>

          {verifiedStatus && verifiedIcon && (
            <Box
              component="img"
              src={verifiedIcon}
              alt="verified"
              sx={{ position: "absolute", bottom: 0, right: -8, width: 14, height: 14 }}
            />
          )}
        </Box>

        {platformIcon}

        <MDTypography
          variant="h6"
          fontWeight="medium"
          sx={{ textTransform: "uppercase", ml: 0.5, color: "#28333f" }}
        >
          {label}
        </MDTypography>
      </MDBox>

      {/* Animated KeyMetrics Section */}
      <AnimatedHeight>
        <KeyMetrics
          profile={profile}
          datasets={datasets}
          previousdatasets={previousdatasets}
          adjustedcurrentdatasets={adjustedcurrentdatasets}
          adjustedpreviousdatasets={adjustedpreviousdatasets}
          selectedCategories={selectedCategories}

          // ⭐ PASS DOWN NEW PROP
          hideKeyMetrics={hideKeyMetrics}
        />
      </AnimatedHeight>
    </Card>
  );
}
